import { BoxGeometry, Mesh, MeshStandardMaterial, type Scene, Vector3 } from 'three';
import type { DummySpec } from '../combat/TargetDummies';
import type { BotRoute } from '../game/bot';
import type { ZoneDef } from '../game/zones';
import type { ScenePalette } from './environment';
import { buildLane, type LaneSpec } from './laneBuilder';
import { loadMaterials } from './materials';
import { mergeStaticMeshes } from './meshMerge';
import { generateBoxUVs, makeSurfPrism } from './ramps';
import type { BuiltLevel } from './TestLevel';

/**
 * "The Proving Grounds" — the first combat arena (spec 0026, ADR 0007): one
 * wide, forgiving surf lane drops from a high spawn onto a big fighting plaza
 * with target dummies (three static, one patrolling) and two low prisms for
 * strafe play. Untimed (no timer zones); a teleport pad at the far edge loops
 * back to the spawn platform. Dusty-gold arena atmosphere.
 */

export const PROVING_GROUNDS_ID = 'arena_proving_grounds';

/** Warm dusty-arena light, distinct from the practice maps. */
export const provingGroundsPalette: ScenePalette = {
  background: 0x3a2e20,
  fogColor: 0x453722,
  fogDensity: 0.00004,
  sunColor: 0xffe0b0,
  sunIntensity: 2.6,
  envIntensity: 1.0,
  ambientColor: 0x5a4a30,
};

/** Drop-in cursor (capsule-feet frame); the lane flows -Z onto the plaza. */
const START = new Vector3(0, 2400, 0);
const LANE: LaneSpec = {
  width: 1200,
  angleDeg: 55,
  declineDeg: 10,
  segments: [{ length: 3500 }],
};
const PLAZA_SIZE = 6000;
const PLAZA_THICKNESS = 48;

/** Slow square patrol loop on the plaza: walk, quarter-turn, repeat. */
function patrolRoute(): BotRoute {
  const leg = (yawDeg: number): BotRoute['phases'] => [
    { kind: 'yawTo', yawDeg, degPerTick: 3, until: { tick: 40 } },
    { kind: 'hold', forward: 1, right: 0, until: { tick: 110 } },
  ];
  return {
    phases: [...leg(0), ...leg(90), ...leg(180), ...leg(270)],
    maxTicks: 600,
  };
}

export async function buildProvingGrounds(scene: Scene): Promise<BuiltLevel> {
  const materials = await loadMaterials();
  const colliders: Mesh[] = [];
  const toMerge: Mesh[] = [];

  const platform = (x: number, y: number, z: number, sizeX: number, sizeZ = sizeX): void => {
    const p = new Mesh(new BoxGeometry(sizeX, PLAZA_THICKNESS, sizeZ), materials.platform);
    p.position.set(x, y, z);
    p.receiveShadow = true;
    scene.add(p);
    toMerge.push(p);
  };

  // The single wide teaching lane down to the plaza.
  const run = buildLane(LANE, START.clone(), 180, materials.ramp);
  for (const mesh of run.meshes) {
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    scene.add(mesh);
    toMerge.push(mesh);
  }

  // Fighting plaza ahead of the lane exit.
  const plaza = new Vector3(run.cursor.x, run.cursor.y - 140, run.cursor.z - PLAZA_SIZE / 2 + 400);
  platform(plaza.x, plaza.y, plaza.z, PLAZA_SIZE);
  const floorY = plaza.y + PLAZA_THICKNESS / 2; // walkable plaza surface

  // Two low side prisms on the plaza for strafe play around the dummies.
  for (const side of [-1, 1]) {
    const prism = new Mesh(
      makeSurfPrism({ length: 2200, width: 560, angleDeg: 55 }),
      materials.ramp,
    );
    generateBoxUVs(prism.geometry);
    prism.position.set(plaza.x + side * 1900, floorY, plaza.z);
    prism.receiveShadow = true;
    scene.add(prism);
    toMerge.push(prism);
  }

  colliders.push(...mergeStaticMeshes(scene, toMerge));

  // Teleport pad back to the top, at the plaza's far edge (small — the plaza
  // itself is the fight space, so the pad must be avoidable).
  // Spawn: a short drop onto the lane's +x face (face height at lateral 250
  // is ~500 above the lane cursor), so you land carving — no standing start.
  const spawn = new Vector3(START.x + 250, START.y + 560, START.z - 300);
  const padCenter = new Vector3(plaza.x, floorY + 200, plaza.z - PLAZA_SIZE / 2 + 400);
  const padSize = new Vector3(700, 500, 700);
  const marker = new Mesh(
    new BoxGeometry(padSize.x, padSize.y, padSize.z),
    new MeshStandardMaterial({
      color: 0xab47bc,
      emissive: 0xab47bc,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.12,
    }),
  );
  marker.position.copy(padCenter);
  scene.add(marker); // visual only — never a collider

  const zones: ZoneDef[] = [
    { kind: 'teleport', center: padCenter, size: padSize, dest: spawn.clone() },
  ];

  // Target dummies: three static in an arc mid-plaza + one patrolling runner.
  const dummies: DummySpec[] = [
    {
      id: 'dummy-a',
      position: new Vector3(plaza.x - 500, floorY, plaza.z + 600),
      maxHealth: 100,
      respawnSec: 3,
    },
    {
      id: 'dummy-b',
      position: new Vector3(plaza.x, floorY, plaza.z + 350),
      maxHealth: 100,
      respawnSec: 3,
    },
    {
      id: 'dummy-c',
      position: new Vector3(plaza.x + 500, floorY, plaza.z + 600),
      maxHealth: 100,
      respawnSec: 3,
    },
    {
      id: 'dummy-runner',
      position: new Vector3(plaza.x, floorY, plaza.z - 700),
      maxHealth: 150,
      respawnSec: 3,
      patrol: { route: patrolRoute(), startYaw: 0 },
    },
  ];

  // The first fight that fights back (spec 0028): a Brakka-kit AI duelist
  // holding the mid-plaza between the dummy arc and the teleport pad.
  const duelist = {
    id: 'duelist-brakka',
    heroId: 'brakka',
    position: new Vector3(plaza.x, floorY, plaza.z - 1600),
    maxHealth: 300,
    respawnSec: 5,
  };

  return {
    mapId: PROVING_GROUNDS_ID,
    colliders,
    spawn,
    spawnYaw: 0, // face -Z, down the lane toward the plaza
    zones,
    killY: plaza.y - 800,
    combat: { dummies, duelist },
  };
}
